/* Common Imports*/

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { useRouter } from "next/router";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	setDataBeforeEditThunk,
	setPropertySnackbarThunk,
	setSectionToEditThunk,
	setSinglePropertyAboutLocationThunk,
	setSinglePropertyAboutThunk,
	setSinglePropertyAgeThunk,
	setSinglePropertyAmenitiesThunk,
	setSinglePropertyAvailabiltyThunk,
	setSinglePropertyCategoryTypeThunk,
	setSinglePropertyDescriptionThunk,
	setSinglePropertyDeveloperNameThunk,
	setSinglePropertyDeveloperSlugThunk,
	setSinglePropertyEstablishedYearThunk,
	setSinglePropertyFloorNoThunk,
	setSinglePropertyFurnishingThunk,
	setSinglePropertyLandAreaThunk,
	setSinglePropertyLandFacingThunk,
	setSinglePropertyMaxBalconiesThunk,
	setSinglePropertyMaxBathroomsThunk,
	setSinglePropertyMaxBedroomsThunk,
	setSinglePropertyMaxCarpetAreaThunk,
	setSinglePropertyMaxFloorsThunk,
	setSinglePropertyMaxPriceThunk,
	setSinglePropertyMaxSuperBuiltUpAreaThunk,
	setSinglePropertyMinBalconiesThunk,
	setSinglePropertyMinBathroomsThunk,
	setSinglePropertyMinBedroomsThunk,
	setSinglePropertyMinCarpetAreaThunk,
	setSinglePropertyMinPriceThunk,
	setSinglePropertyMinSuperBuiltUpAreaThunk,
	setSinglePropertyNoOfSeatsThunk,
	setSinglePropertyNoOfUnitsThunk,
	setSinglePropertyOtherPlotDimensionsThunk,
	setSinglePropertyOverviewThunk,
	setSinglePropertyPlotDimensionsThunk,
	setSinglePropertyPossessionDateThunk,
	setSinglePropertySecurityDepositThunk,
	setSinglePropertySubTypeThunk,
	setSinglePropertyTagsThunk,
	setSinglePropertyTransactionTypeThunk,
	setSinglePropertyTypeThunk,
	setSinglePropertyUnitFacingThunk,
	setSinglePropertyVicinityThunk,
	UpdateAboutDeveloperThunk,
} from "../../../../../redux-magic/store";

/* Components Import */
import {
	Alert,
	Autocomplete,
	CircularProgress,
	Divider,
	IconButton,
	Link,
	Paper,
	Skeleton,
	Snackbar,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Icon Imports */

import { Close, EditOutlined } from "@mui/icons-material";
import {
	PropertyMediaImage,
	PropertyMediaVideo,
	PropertyMediaVideoMetadata,
} from "../../../../../redux-magic/sub-interfaces/property-interface";
import { confirmSendForReview, skipNextSendForReviewConfirm } from "../../SendForReviewConfirm";
import { confirmDiscardChanges, useEditSnapshot } from "../../SectionDiscardConfirm";

/* Styled Components */

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const Container = styled(Paper)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	borderRadius: "16px",
	boxShadow: "none",
	background: theme.palette.background.paper,
	padding: "0.875rem",
	[theme.breakpoints.up("sm")]: {
		padding: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem",
	},
}));

const Contain = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	margin: "0rem 0rem 0.25rem 0rem",
	[theme.breakpoints.up("md1190")]: {
		margin: "0rem 0rem 0.5rem 0rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const HeadingTwo = styled(Typography)(({ theme }) => ({
	margin: "0rem 0rem 0rem 0rem",
	width: "100%",
	fontWeight: "500",
	fontSize: "0.825rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const SubHeading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "400",
	fontSize: "0.825rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
	display: "inline",
	overflowWrap: "break-word",
}));

const Column = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
}));

const EditColumn = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "1rem",
	margin: "0rem 0rem 0rem 0rem",
}));

const ShowMore = styled(Typography)(({ theme }) => ({
	margin: "0.25rem 0rem 0rem 0rem",
	width: "100%",
	fontWeight: "500",
	color: theme.palette.primary.main,
	textAlign: "center",
	fontSize: "0.85rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
		margin: "0.5rem 0rem 0rem 0rem",
	},
}));

const ContactName = styled(Link)(({ theme }) => ({
	fontWeight: "400",
	fontSize: "0.875rem",

	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
	color: theme.palette.mode == "dark" ? "#ffffff" : "#000000",
	textDecoration: "none",
	textAlign: "left",
	"&:hover": {
		color: theme.palette.primary.main,
	},
	// [theme.breakpoints.up("md")]: {
	// 	textAlign: "center",
	// },
}));

// const BottomContainer = styled("div")(({ theme }) => ({
//   display: "flex",
//   flexDirection: "column",
//   justifyContent: "space-between",
//   alignItems: "flex-start",
//   width: "100%",
//   gap: "0rem",
// }));

const Snack = styled(Snackbar)(({ theme }) => ({
	// [theme.breakpoints.down("md")]: {
	// 	bottom: "5rem",
	// 	borderRadius: "0.5rem",
	// },
	// marginTop: "2.8rem",
	// [theme.breakpoints.down("sm")]: {
	// 	marginTop: "3.8rem",
	// },
}));

// const BottomRow = styled("div")(({ theme }) => ({
//   display: "flex",
//   flexDirection: "row",
//   justifyContent: "space-between",
//   alignItems: "center",
//   width: "100%",
//   gap: "1rem",
//   margin: "0rem 0rem 0rem 0rem",
//   [theme.breakpoints.down("md")]: {
//     flexDirection: "column",
//   },
// }));

const OptionContainer = styled("li")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "flex-start",
	width: "100%",
	padding: "0.25rem 0.5rem",
}));

const OptionText = styled(Typography, {
	shouldForwardProp: (prop) => prop !== "isPrimary",
})<any>(({ theme, isPrimary }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	fontSize: "0.875rem",
	lineHeight: "1.3125rem",
	fontWeight: 400,
	color: isPrimary ? theme.palette.primary.main : theme.palette.text.primary,
})) as any;

interface LocalBuilderInterface {
	name: string;
	slug: string;
	description: string;
	year_est: number;
	total_projects: number;
	ongoing_projects: number;
	completed_projects: number;
	upcoming_projects: number;
	logo: {
		_id: ObjectId | string;
		file_id: ObjectId | string;
		url?: PropertyMediaImage | PropertyMediaVideo | string;
		video_metadata?: PropertyMediaVideoMetadata;
		mime_type?: string;
		caption: string;
		original_file_server_path: string;
	};
}

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const AboutDeveloperCard = ({
	session,
	about_developer,
	property_created_by,
	search_metrics,
	property_id,
	dispatch,
	negativeScroll,
	isEditor,
	profile_context,
	loading,
	section_to_edit,
	data_before_edit,
	assigned_to,
	developer_snackbar,
	developers,
	screenType,
}: {
	about_developer: LocalBuilderInterface;
	property_created_by: LocalPropertyInterface["created_by"];
	search_metrics: PropertyInterface["search_metrics"];
	property_id: string | ObjectId;
	dispatch: Function;
	session: any;
	negativeScroll: boolean;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	loading: boolean;
	section_to_edit: string;
	data_before_edit: any;
	assigned_to: StateInterface["single_property_page"]["property"]["assigned_to"];
	developer_snackbar: boolean;
	developers: BusinessProfileInterface[];
	screenType: string;
}) => {
	const theme = useTheme();

	/*

  & Let's declare the router. Here we are using the useRouter hook from nextjs.

  & We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

  */

	const router = React.useRef(useRouter()).current;

	const [autocompleteValue, setAutocompleteValue] = React.useState<string | null>(null);
	const [options, setOptions] = React.useState<any[]>([]);

	const localLang = {
		aboutDeveloper:
			router.locale === "kn-IN" ? "ಡೆವಲಪರ್ ಬಗ್ಗೆ" : router.locale === "ar-AE" ? "عن المطور" : "About Developer",
		snackValue:
			router.locale === "kn-IN"
				? "ನವೀಕರಣ ಯಶಸ್ವಿಯಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "تم تحديث تفاصيل مطور العقار بنجاح"
					: "Property developer details updated successfully",

		establishedIn:
			router.locale === "kn-IN" ? "ಸ್ಥಾಪಿತ ವರ್ಷ" : router.locale === "ar-AE" ? "تأسس في" : "Established in",

		developerName:
			router.locale === "ar-AE" ? "اسم المطور" : router.locale === "kn-IN" ? "ಡೆವಲಪರ್ ಹೆಸರು" : "Developer name",
		establishedYear:
			router.locale === "ar-AE" ? "سنة التأسيس" : router.locale === "kn-IN" ? "ಸ್ಥಾಪಿತ ವರ್ಷ" : "Established year",
		description: router.locale === "ar-AE" ? "الوصف" : router.locale === "kn-IN" ? "ವಿವರಣೆ" : "Description",

		update: router.locale === "kn-IN" ? "ಅಪ್ಡೇಟ್ ಮಾಡಿ" : router.locale === "ar-AE" ? "تحديث" : "Update",
		seeMore: router.locale === "kn-IN" ? "ಮತ್ತಷ್ಟು ತೋರಿಸಿ" : router.locale === "ar-AE" ? "رؤية المزيد" : "See more",
		seeLess: router.locale === "kn-IN" ? "ಕಡಿಮೆ ತೋರಿಸಿ" : router.locale === "ar-AE" ? "رؤية أقل" : "See less",
	};

	const [showMore, setShowMore] = React.useState<Boolean>(false);

	const [isEditing, setIsEditing] = React.useState(false);
	const isSectionDirty = useEditSnapshot(isEditing);

	const [load, setLoad] = React.useState<boolean>(false);

	const [builder, setBuilder] = React.useState<any>(null);

	const creatorType: string = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id !== ""
				? (property_created_by.business_profile_id as BusinessProfileInterface).business_profile_type
				: "user"
			: "user"
		: "user";

	const newDelimitedSearchString = search_metrics ? search_metrics.search_string_delimiter.split("ߐ") : [];

	newDelimitedSearchString[6] = about_developer.description;

	React.useEffect(() => {
		if (section_to_edit !== "property-developer") {
			setIsEditing(false);
		}
	}, [data_before_edit, section_to_edit]);

	const profile_context_user = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: null;

	if (loading) {
		return (
			<Container
				id="about-developer"
				sx={{ scrollMarginTop: negativeScroll === true ? "4rem" : "8rem" }}
			>
				<Contain>
					<Heading>
						<Skeleton
							animation="wave"
							variant="text"
							width="15rem"
						/>
					</Heading>
				</Contain>
				<Contain>
					<Heading>
						<Skeleton
							animation="wave"
							variant="text"
							width="10rem"
						/>
						<Skeleton
							animation="wave"
							variant="text"
							width="10rem"
							height="1rem"
						/>
					</Heading>
				</Contain>
				<Contain>
					<Heading>
						<Skeleton
							animation="wave"
							variant="text"
							width="100%"
							height="1rem"
						/>
						<Skeleton
							animation="wave"
							variant="text"
							width="100%"
							height="1rem"
						/>
						<Skeleton
							animation="wave"
							variant="text"
							width="100%"
							height="1rem"
						/>
						<Skeleton
							animation="wave"
							variant="text"
							width="100%"
							height="1rem"
						/>
					</Heading>
				</Contain>
			</Container>
		);
	}

	const aboutDeveloperContainer = document.getElementById("about-developer");

	const submitForm = async () => {
		/* Saving unlists the listing until it is approved again: say so first (gap A3, the app's prompt). */
		if (!(await confirmSendForReview())) return;
		
		setLoad(true);
		await dispatch(
			UpdateAboutDeveloperThunk({
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				developer_name: about_developer.name,
				developer_slug: about_developer.slug,
				description: about_developer.description,
				year_est: about_developer.year_est,
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				delimited_search_string: newDelimitedSearchString.join("ߐ"),
				search_string: newDelimitedSearchString.join(""),
				property_id: property_id,
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: property_created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (property_created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (property_created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
				profile_context: profile_context,
			}),
		);
		dispatch(setSectionToEditThunk(""));
		dispatch(setDataBeforeEditThunk({}));
		setIsEditing(false);
		aboutDeveloperContainer
			? aboutDeveloperContainer.scrollIntoView({
					behavior: "smooth",
					block: "center",
					inline: "start",
				})
			: null;
		setLoad(false);
	};

	const logged_user: ObjectId | string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const logged_user_access: string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser
					? session.user.dbUser.access_level
						? session.user.dbUser.access_level
						: ""
					: ""
				: ""
			: ""
		: "";

	// const creator_id: ObjectId | string = property_created_by
	//   ? property_created_by.is_business_profile
	//     ? property_created_by.business_profile_id != ""
	//       ? (property_created_by.business_profile_id as BusinessProfileInterface)
	//           ._id
	//       : property_created_by.user_id != ""
	//         ? (property_created_by.user_id as UserInterface)._id
	//         : ""
	//     : property_created_by.user_id != ""
	//       ? (property_created_by.user_id as UserInterface)._id
	//       : ""
	//   : "";

	const creator_user_id: string | ObjectId | UserInterface | BusinessProfileInterface = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: (property_created_by.business_profile_id as string)
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: (property_created_by.user_id as string)
		: "";

	const is_user_creator: boolean =
		profile_context_user !== null
			? creator_user_id !== ""
				? profile_context_user === creator_user_id
					? true
					: false
				: false
			: false;

	const handleAutocompleteChange = async (value: any) => {
		setAutocompleteValue(value);
		if (value) {
			dispatch(setSinglePropertyDeveloperNameThunk(value.builder.name));
			dispatch(setSinglePropertyDeveloperSlugThunk(value.builder.slug));
			dispatch(setSinglePropertyEstablishedYearThunk(value.builder.year_est));
			dispatch(setSinglePropertyDescriptionThunk(value.builder.description));
		}
	};

	const handleAutocompleteInputChange = async (search_term: string) => {
		/*

		& Next, let's describe a debounced, asynchronous function that will be called when the company name changes.

		*/

		const debouncedExecutor: Function = async (): Promise<void> => {
			const response = await (
				await fetch(
					process.env.PRODUCTION_API_URL +
						"/properties/edit-property/fetch-autocomplete-developer?search_term=" +
						search_term,
					{
						cache: "no-store",
						headers: { "Content-Type": "application/json" },
						method: "POST",
					},
				)
			).json();

			if (response.length > 0) {
				setOptions(response);
			} else {
				setOptions([]);
			}
		};

		/*

		& Next, let's use setTimeout to debounce the function call.

		*/

		const debouncer: number = setTimeout(debouncedExecutor, 500);

		/*

		& Next, let's return a cleanup function that will clear the timeout.

		*/

		return (): void => clearTimeout(debouncer);
	};

	return (
		<Container
			id={screenType + "_spp_about-developer"}
			sx={{ scrollMarginTop: negativeScroll === true ? "4rem" : "8rem" }}
		>
			<Snack
				open={developer_snackbar}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{localLang.snackValue}
				</Alert>
			</Snack>

			<Contain>
				<Heading>{localLang.aboutDeveloper}</Heading>

				{is_user_creator ||
				(logged_user_access.includes("admin") &&
					logged_user_access.includes("property") &&
					assigned_to.includes(logged_user)) ||
				logged_user_access.includes("super_admin") ||
				isEditor ? (
					<React.Fragment>
						{!isEditing ? (
							<IconButton
								id={screenType + "_property_about_developer_edit_button"}
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									dispatch(setSectionToEditThunk("property-developer"));
									if (section_to_edit !== "property-developer") {
										switch (section_to_edit) {
											case "property-details":
												dispatch(setSinglePropertyCategoryTypeThunk(data_before_edit.property_category_type));
												dispatch(setSinglePropertyTransactionTypeThunk(data_before_edit.property_transaction_type));
												dispatch(setSinglePropertyTypeThunk(data_before_edit.property_type));
												dispatch(setSinglePropertySubTypeThunk(data_before_edit.property_subtype));
												dispatch(setSinglePropertyMinPriceThunk(data_before_edit.property_min_price));
												dispatch(setSinglePropertyMaxPriceThunk(data_before_edit.property_max_price));
												dispatch(setSinglePropertySecurityDepositThunk(data_before_edit.property_security_deposit));
												dispatch(setSinglePropertyAvailabiltyThunk(data_before_edit.property_availability));
												dispatch(setSinglePropertyPossessionDateThunk(data_before_edit.property_possession_date));
												dispatch(setSinglePropertyTagsThunk(data_before_edit.property_tags));
												dispatch(setSinglePropertyUnitFacingThunk(data_before_edit.property_unit_facing));
												dispatch(setSinglePropertyLandFacingThunk(data_before_edit.property_land_facing));
												dispatch(setSinglePropertyAgeThunk(data_before_edit.property_age));
												break;
											case "property-config":
												dispatch(setSinglePropertyFurnishingThunk(data_before_edit.property_furnishing_status));
												dispatch(setSinglePropertyLandAreaThunk(data_before_edit.property_area));
												dispatch(setSinglePropertyMaxBalconiesThunk(data_before_edit.property_balconies_max));
												dispatch(setSinglePropertyMaxBathroomsThunk(data_before_edit.property_bathroom_max));
												dispatch(setSinglePropertyMaxBedroomsThunk(data_before_edit.property_bhk_max));
												dispatch(setSinglePropertyMaxCarpetAreaThunk(data_before_edit.property_carpet_area_max));
												dispatch(setSinglePropertyMaxFloorsThunk(data_before_edit.property_floors_max));
												dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(data_before_edit.super_builtup_max));
												dispatch(setSinglePropertyMinBalconiesThunk(data_before_edit.property_balconies_min));
												dispatch(setSinglePropertyMinBathroomsThunk(data_before_edit.property_bathroom_min));
												dispatch(setSinglePropertyMinBedroomsThunk(data_before_edit.property_bedroom_min));
												dispatch(setSinglePropertyMinCarpetAreaThunk(data_before_edit.property_carpet_area_mix));
												dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(data_before_edit.super_builtup_min));
												dispatch(setSinglePropertyNoOfUnitsThunk(data_before_edit.property_no_of_units));
												dispatch(setSinglePropertyNoOfSeatsThunk(data_before_edit.property_no_of_seats));
												dispatch(setSinglePropertyFloorNoThunk(data_before_edit.property_floor_no));
												dispatch(setSinglePropertyOtherPlotDimensionsThunk(data_before_edit.other_plot_dimensions));
												dispatch(setSinglePropertyPlotDimensionsThunk(data_before_edit.property_plot_dimensions));
												break;
											case "property-overview":
												dispatch(setSinglePropertyOverviewThunk(data_before_edit));
												break;
											case "property-amenities":
												dispatch(setSinglePropertyAmenitiesThunk(data_before_edit));
												break;
											case "about-property":
												dispatch(setSinglePropertyAboutThunk(data_before_edit));
												break;
											case "property-location":
												dispatch(setSinglePropertyAboutLocationThunk(data_before_edit));
												break;
											case "property-vicinity":
												dispatch(setSinglePropertyVicinityThunk(data_before_edit));
												break;
											default:
												break;
										}
										dispatch(setSectionToEditThunk("property-developer"));
									}
									setIsEditing(true);
									dispatch(
										setDataBeforeEditThunk({
											name: about_developer.name,
											description: about_developer.description,
											year_est: about_developer.year_est,
										}),
									);
									setAutocompleteValue(null);
								}}
							>
								<EditOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						) : (
							<IconButton
								id={screenType + "_property_about_developer_close_button"}
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={async () => {
									/* Unsaved changes ask first (3 Nov 2026, the app's "Discard changes?"). */
									if (isSectionDirty()) {
										const answer = await confirmDiscardChanges();
										if (answer === "keep") return;
										if (answer === "save") {
											skipNextSendForReviewConfirm();
											await submitForm();
											return;
										}
									}
									setIsEditing(false);
									dispatch(setSinglePropertyDeveloperNameThunk(data_before_edit.name));
									dispatch(setSinglePropertyDescriptionThunk(data_before_edit.description));
									dispatch(setSinglePropertyEstablishedYearThunk(data_before_edit.year_est));
									setAutocompleteValue(null);
								}}
							>
								<Close
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						)}
					</React.Fragment>
				) : null}
			</Contain>

			{!isEditing ? (
				/* User View */

				<Column>
					{about_developer.name ? (
						creatorType === "developer" ? (
							<ContactName
								href={
									(process.env.PRODUCTION_URL || " ") +
									router.locale +
									"/" +
									creatorType +
									"s" +
									"/" +
									about_developer.slug
								}
								target="_blank"
								rel="noopener noreferrer"
								id={screenType + "_spp_about_developer_name"}
								sx={{
									margin: "0.125rem 0rem 0.5rem 0rem",
									color: theme.palette.text.primary,
								}}
							>
								{about_developer.name}
							</ContactName>
						) : (
							<HeadingTwo>{about_developer.name}</HeadingTwo>
						)
					) : null}

					{about_developer.year_est === 0 ? null : (
						<SubHeading
							sx={{
								margin: "0.125rem 0rem 0.5rem 0rem",
								color: theme.palette.text.secondary,
							}}
						>
							{localLang.establishedIn + " " + about_developer.year_est}
						</SubHeading>
					)}

					{about_developer.description ? (
						<>
							<SubHeading variant="body1">
								{showMore
									? about_developer.description
									: about_developer.description.length > 150
										? about_developer.description.substring(0, 150) + "..."
										: about_developer.description}
							</SubHeading>

							{about_developer.description.length > 150 && (
								<ShowMore
									id={screenType + "_spp_about_developer_see_more_button"}
									onClick={() => setShowMore(!showMore)}
									sx={{ cursor: "pointer" }}
								>
									{showMore ? localLang.seeLess : localLang.seeMore}
								</ShowMore>
							)}
						</>
					) : null}
				</Column>
			) : (
				/* Edit View */

				<EditColumn>
					{load ? undefined : (
						<React.Fragment>
							<Autocomplete
								fullWidth
								disablePortal
								sx={{ mb: "10px" }}
								id={screenType + "_spp_about_developer_autocomplete"}
								options={options}
								value={autocompleteValue}
								onChange={(event, value) => handleAutocompleteChange(value)}
								getOptionLabel={(option) => option.builder.name}
								getOptionKey={(option) => option.id}
								onInputChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									if (event !== null) {
										handleAutocompleteInputChange(event.target.value);
									}
								}}
								renderOption={(props, option, state, ownerState) => {
									return (
										<OptionContainer
											{...props}
											key={option.id}
										>
											<OptionText
												variant="body1"
												component="span"
											>
												{option.builder.name}
											</OptionText>
										</OptionContainer>
									);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										fullWidth
										label="Select developer to use details"
									/>
								)}
							/>
							<Divider sx={{ mb: "10px" }} />
						</React.Fragment>
					)}

					<TextField
						id={screenType + "_spp_about_developer_name"}
						value={about_developer.name}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							if (section_to_edit !== "property-developer") {
								dispatch(setSectionToEditThunk("property-developer"));
							}
							dispatch(setSinglePropertyDeveloperNameThunk(event.target.value));
							dispatch(setSinglePropertyDeveloperSlugThunk(event.target.value.toLowerCase().replace(/\s+/g, "-")));
						}}
						label={localLang.developerName}
						autoComplete="off"
						sx={{
							width: "100%",
							"& .MuiInputBase-input": {
								fontSize: "0.825rem",
								[theme.breakpoints.up("sm")]: {
									fontSize: "0.875rem",
								},
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1rem",
								},
							},
						}}
					/>

					<TextField
						id={screenType + "_spp_about_developer_year_est"}
						value={about_developer.year_est === 0 ? "" : about_developer.year_est}
						slotProps={{
							htmlInput: {
								inputMode: "numeric",
								pattern: "[0-9]*",
							},
						}}
						onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
							if (event.key === "Enter" || event.key === "Next") {
								(event.target as HTMLInputElement).blur();
							}
						}}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							if (section_to_edit !== "property-developer") {
								dispatch(setSectionToEditThunk("property-developer"));
							}
							dispatch(setSinglePropertyEstablishedYearThunk(Number(event.target.value.replace(/\D/g, ""))));
						}}
						label={localLang.establishedYear}
						autoComplete="off"
						sx={{
							width: "100%",
							"& .MuiInputBase-input": {
								fontSize: "0.825rem",
								[theme.breakpoints.up("sm")]: {
									fontSize: "0.875rem",
								},
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1rem",
								},
							},
						}}
					/>

					<TextField
						id={screenType + "_spp_about_developer_description"}
						value={about_developer.description}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							if (section_to_edit !== "property-developer") {
								dispatch(setSectionToEditThunk("property-developer"));
							}
							dispatch(setSinglePropertyDescriptionThunk(event.target.value));
						}}
						label={localLang.description}
						multiline
						maxRows={10}
						fullWidth
						autoComplete="off"
						sx={{
							width: "100%",
							"& .MuiInputBase-input": {
								fontSize: "0.825rem",
								[theme.breakpoints.up("sm")]: {
									fontSize: "0.875rem",
								},
								[theme.breakpoints.up("md1190")]: {
									fontSize: "1rem",
								},
							},
						}}
					/>
				</EditColumn>
			)}

			{/* Update Button */}

			{!isEditing ? null : (
				<>
					{load ? (
						<CircularProgress
							// size={24}
							sx={{
								marginTop: "1rem",
							}}
						/>
					) : (
						<BeegruButton
							id={screenType + "_spp_about_developer_update_button"}
							sx={{ margin: "1rem 0rem 0rem 0rem", width: "100%" }}
							flavor="primary"
							onClick={submitForm}
							variant="contained"
							type="button"
							name="Update"
						>
							{localLang.update}
						</BeegruButton>
					)}
				</>
			)}
		</Container>
	);
};

export default AboutDeveloperCard;
